import { FundingOpportunities } from '@platform/client';
import { Button } from '@platform/components';
import { keepPreviousData } from '@tanstack/react-query';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { FundingOpportunity } from 'lib/client/src/platformapi.schemas';
import { useCallback, useMemo, useState, useContext } from 'react';
import { CreateFundingOpportunityModal } from './CreateFundingOpportunityModal';
import { DeleteFundingOpportunityModal } from './DeleteFundingOpportunityModal';
import { UpdateFundingOpportunityModal } from './UpdateFundingOpportunityModal';
import { AdminTable, AdminTableContext } from '../../AdminTable';
import { AdminListPageWrapper } from '../../AdminListPage';

const FundingOpportunitiesAdmin = () => {
  return (
    <AdminListPageWrapper title={'Funding Opportunities'}>
      <FundingOpportunitiesAdminContent />
    </AdminListPageWrapper>
  );
};

const columnHelper = createColumnHelper<FundingOpportunity>();

const FundingOpportunitiesAdminContent = () => {
  const { adminQueryOptions, setPagination } = useContext(AdminTableContext);
  const [isCreateFundingOpportunityOpen, setIsCreateFundingOpportunityOpen] = useState(false);
  const [openFundingOpportunity, setOpenFundingOpportunity] = useState<
    FundingOpportunity | undefined
  >();
  const [fundingOpportunityToDelete, setFundingOpportunityToDelete] = useState<number | undefined>(
    undefined,
  );

  const { data: fundingOpportunityCount = adminQueryOptions.page } =
    FundingOpportunities.useCountFundingOpportunitiesAdmin();

  const {
    data: fundingOpportunities = [],
    isLoading,
    isError,
    isSuccess,
  } = FundingOpportunities.useGetFundingOpportunitiesAdmin(
    {
      page: adminQueryOptions.page,
      limit: adminQueryOptions.limit,
      sort: adminQueryOptions.sort,
    },
    {
      query: {
        placeholderData: keepPreviousData,
      },
    },
  );

  const openFundingOpportunityModal = useCallback(
    (fundingOpportunityId: number) => {
      setOpenFundingOpportunity(
        fundingOpportunities.find(
          (fundingOpportunity) => fundingOpportunity.id === fundingOpportunityId,
        ),
      );
    },
    [fundingOpportunities],
  );

  const openDeleteFundingOpportunityModal = (fundingOpportunityId: number) => {
    setFundingOpportunityToDelete(fundingOpportunityId);
  };

  const closeFundingOpportunityModal = () => {
    setOpenFundingOpportunity(undefined);
    setIsCreateFundingOpportunityOpen(false);
  };

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor('id', {
          cell: (info) => info.getValue(),
          header: () => <span>Id</span>,
          size: 50,
          enableSorting: true,
        }),
        columnHelper.accessor('title', {
          cell: (info) => info.getValue(),
          header: () => <span>Title</span>,
          size: 150,
          enableSorting: true,
        }),
        columnHelper.accessor('description', {
          cell: (description) => {
            let descriptionText = description.getValue();
            if (descriptionText.length > 150) {
              descriptionText = description.getValue().substring(0, 150) + '...';
            }
            return descriptionText;
          },
          header: () => 'Description',
          size: 350,
          maxSize: 500,
          enableSorting: false,
        }),
        columnHelper.accessor('link', {
          cell: (info) => <div className='text-wrap w-[150px] break-words'>{info.getValue()}</div>,
          header: () => <span>Link</span>,
          size: 150,
          enableSorting: false,
        }),
        columnHelper.accessor('date', {
          cell: (date) => new Date(date.getValue()).toLocaleDateString(),
          header: () => <span>Date</span>,
          size: 150,
          enableSorting: true,
        }),
        columnHelper.accessor('createdAt', {
          cell: (createdAt) => new Date(createdAt.getValue()).toLocaleDateString(),
          header: () => <span>Created At</span>,
          size: 150,
          enableSorting: true,
        }),
        columnHelper.display({
          id: 'actions',
          cell: (props) => (
            <Button
              additionalClassnames='bg-white'
              variant='secondary-gray'
              size='sm'
              onPress={() => {
                openFundingOpportunityModal(props.row.getValue('id'));
              }}
            >
              Update
            </Button>
          ),
          size: 50,
        }),
        columnHelper.display({
          id: 'Delete',
          cell: (props) => (
            <Button
              variant='primary-danger'
              size='sm'
              onPress={() => {
                openDeleteFundingOpportunityModal(props.row.getValue('id'));
              }}
            >
              Delete
            </Button>
          ),
          size: 50,
        }),
      ] as ColumnDef<FundingOpportunity, unknown>[],
    [openFundingOpportunityModal],
  );

  return (
    <>
      <AdminTable
        data={fundingOpportunities}
        columns={columns}
        filteredCount={fundingOpportunityCount}
        isLoading={isLoading}
        isError={isError}
        isSuccess={isSuccess}
      />
      <Button
        additionalClassnames='md:w-[250px] md:max-w-[250px]'
        variant='primary'
        size='md'
        onPress={() => setIsCreateFundingOpportunityOpen(true)}
      >
        Create Funding Opportunity
      </Button>
      <CreateFundingOpportunityModal
        isOpen={isCreateFundingOpportunityOpen}
        onClose={closeFundingOpportunityModal}
        resetPagination={() =>
          setPagination({ pageIndex: adminQueryOptions.page, pageSize: adminQueryOptions.limit })
        }
        limit={adminQueryOptions.limit}
      />
      {fundingOpportunityToDelete && (
        <DeleteFundingOpportunityModal
          isOpen={!!fundingOpportunityToDelete}
          onClose={() => setFundingOpportunityToDelete(undefined)}
          id={fundingOpportunityToDelete}
          page={adminQueryOptions.page}
          limit={adminQueryOptions.limit}
        />
      )}
      {openFundingOpportunity && (
        <UpdateFundingOpportunityModal
          fundingOpportunity={openFundingOpportunity}
          isOpen={!!openFundingOpportunity}
          onClose={closeFundingOpportunityModal}
          page={adminQueryOptions.page}
          limit={adminQueryOptions.limit}
        />
      )}
    </>
  );
};

export default FundingOpportunitiesAdmin;
